import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import LoadingButton from "@mui/lab/LoadingButton";
import { Button, Fade, Grid, Stack, TextField, Typography } from "@mui/material";

import { LOGO } from "Configs/core";

import { useAppDispatch } from "Hooks/useRedux";
import useWindowSize from "Hooks/useWindowSize";

import { login } from "Redux/authSlice";

import Api from "Api";

import Image from "Assets/Images/Login/image.png";

type Step = "Login" | "Verify";
interface IFormData {
  mobile?: string;
  otp?: string;
}

export default function Login() {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const [step, setStep] = useState<Step>("Login");
  const [showForm, setShowForm] = useState<boolean>(true);
  const [formData, setFormData] = useState<IFormData>({});
  const [timer, setTimer] = useState<number>(60);

  const { height } = useWindowSize();

  const handleSaveFormData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleChangeStep = (goTo: Step) => {
    setShowForm(false);

    setTimeout(() => {
      setStep(goTo);
      setShowForm(true);
    }, 500);
  };

  const loginMutation = useMutation({
    mutationFn: (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      return Api.post("user/login", { ...formData, type: "otp" }).then((res) => {
        handleChangeStep("Verify");
      });
    },
  });
  const verifyMutation = useMutation({
    mutationFn: (e: React.FormEvent<HTMLFormElement> | null) => {
      if (e) e.preventDefault();

      return Api.post("user/verify", { ...formData }).then((res) => {
        dispatch(login(res.data.Data.token));
      });
    },
  });

  const FormContents = {
    Login: {
      input: { name: "mobile", label: "شماره تلفن همراه" },
      buttonText: "دریافت کد فعالسازی",
      mutation: loginMutation,
    },
    Verify: { input: { name: "otp", label: "کد فعالسازی" }, buttonText: "ورود", mutation: verifyMutation },
  };

  const content = FormContents[step];

  useEffect(() => {
    document.getElementById(`input-${step}`)?.focus();

    let intervalId: ReturnType<typeof setInterval>;
    if (step === "Verify") {
      intervalId = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [step]);
  useEffect(() => {
    if (step === "Verify" && formData.otp?.length === 4) {
      verifyMutation.mutate(null);
    }
  }, [formData.otp]);

  return (
    <Grid container justifyContent="space-between" alignItems="center" sx={{ minHeight: `${height}px` }}>
      <Grid item xs={12} md={7} lg={8}>
        <Stack alignItems="center" spacing={1}>
          <img src={LOGO} alt="logo" />

          <Typography variant="h5" sx={{ fontWeight: "700", color: "#008275" }}>
            شهرداری اصفهان
          </Typography>
          <Typography sx={{ color: "grey.700" }}>سامانه تعریف خطوط ناوگان حمل و نقل عمومی</Typography>
        </Stack>

        <Grid container justifyContent="center" mt={4}>
          <Grid item xs={10} md={6}>
            <Fade in={showForm}>
              <div>
                <form onSubmit={content.mutation.mutate}>
                  <Stack spacing={1} sx={{ maxWidth: "500px", mx: "auto" }}>
                    <TextField
                      id={`input-${step}`}
                      type="number"
                      name={content.input.name}
                      label={content.input.label}
                      value={formData[`${content.input.name}` as keyof typeof formData] || ""}
                      onChange={handleSaveFormData}
                      variant="outlined"
                      fullWidth
                    />
                    <LoadingButton size="large" variant="contained" type="submit" loading={content.mutation.isLoading}>
                      {content.buttonText}
                    </LoadingButton>
                  </Stack>
                </form>

                {step === "Verify" && (
                  <Stack alignItems="center" mt={1}>
                    <Button disabled={timer > 0}>
                      <Typography variant="caption" color="common.black" className="fa-number">{`ارسال مجدد کد فعالسازی ${
                        timer > 0 ? `(${timer} ثانیه)` : ""
                      }`}</Typography>
                    </Button>
                  </Stack>
                )}
              </div>
            </Fade>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6} md={5} lg={4} sx={{ display: { xs: "none", md: "block" } }}>
        <img
          src={Image}
          alt="login"
          style={{
            height: "100vh",
            width: "100%",
            display: "block",
            objectFit: "cover",
          }}
        />
      </Grid>
    </Grid>
  );
}
